import React, { useRef, useState } from "react";
import { Loader2, Upload } from "lucide-react";
import { toast } from "react-toastify";
import services from "../../../API";

const getUploadedAsset = (response, file) => {
  const asset = response?.data?.asset || response?.asset || response?.data || response;
  const url = asset?.url || response?.data?.url || response?.url;

  if (!url) return null;

  return {
    ...asset,
    id: asset?._id || asset?.id || `${file.name}-${file.lastModified}`,
    name: asset?.name || file.name,
    type: asset?.type || file.type.split("/")[0] || "asset",
    mimeType: asset?.mimeType || file.type,
    url,
    preview: url,
    isFromLibrary: true,
  };
};

export default function SceneAssetDropzone({
  onUploaded,
  allowedTypes = ["image"],
  disabled = false,
}) {
  const inputRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  const uploadFiles = async (files) => {
    const acceptedFiles = Array.from(files || []).filter((file) =>
      allowedTypes.some((type) => file.type.startsWith(`${type}/`))
    );
    if (acceptedFiles.length === 0) {
      toast.error(`Please choose ${allowedTypes.join(" or ")} file${allowedTypes.length === 1 ? "" : "s"}.`);
      return;
    }

    setIsUploading(true);
    const uploadedAssets = [];

    try {
      for (const file of acceptedFiles) {
        const formData = new FormData();
        formData.append("file", file);

        const response = await services.uploadImage(formData);
        const asset = getUploadedAsset(response, file);
        if (!asset) throw new Error(`The upload response did not include a URL for ${file.name}.`);
        uploadedAssets.push(asset);
      }

      await onUploaded?.(uploadedAssets);
      toast.success(`${uploadedAssets.length} asset${uploadedAssets.length === 1 ? "" : "s"} uploaded.`);
    } catch (error) {
      console.error("Error uploading scene assets:", error);
      toast.error("Failed to upload assets.");
    } finally {
      setIsUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setIsDragging(false);
    if (!disabled && !isUploading) uploadFiles(event.dataTransfer.files);
  };

  const handleDragLeave = (event) => {
    if (!event.currentTarget.contains(event.relatedTarget)) setIsDragging(false);
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      inputRef.current?.click();
    }
  };

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept={allowedTypes.map((type) => `${type}/*`).join(",")}
        multiple
        className="hidden"
        onChange={(event) => uploadFiles(event.target.files)}
      />
      <button
        type="button"
        disabled={disabled || isUploading}
        onClick={() => inputRef.current?.click()}
        onDragEnter={(event) => {
          event.preventDefault();
          if (!disabled && !isUploading) setIsDragging(true);
        }}
        onDragOver={(event) => {
          event.preventDefault();
          if (!disabled && !isUploading) setIsDragging(true);
        }}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onKeyDown={handleKeyDown}
        className={`flex w-full cursor-pointer flex-col items-center justify-center rounded-2xl border-2 border-dashed px-6 py-5 text-center transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${isDragging
          ? "border-orange-500 bg-orange-50 text-orange-600 dark:bg-orange-500/10 dark:text-orange-400"
          : "border-gray-200 bg-white text-gray-500 hover:border-orange-300 dark:border-white/10 dark:bg-[#111111] dark:text-gray-400 dark:hover:border-orange-500/40"
          }`}
        aria-label="Upload image elements"
      >
        {isUploading ? <Loader2 className="mb-2 h-7 w-7 animate-spin text-orange-500" /> : <Upload className="mb-2 h-7 w-7 text-orange-500" />}
        <span className="text-sm font-semibold leading-5 text-gray-700 dark:text-gray-300">
          {isUploading
            ? "Uploading to asset library..."
            : isDragging
              ? "Drop files to upload"
              : "Drag and drop files here to upload"}
        </span>
        {!isUploading && (
          <span className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            {allowedTypes.map((type) => `${type[0].toUpperCase()}${type.slice(1)}`).join(", ")} files are supported.
          </span>
        )}
      </button>
    </>
  );
}
